'use client';
import React from 'react';
import {
	AbsoluteFill,
	Audio,
	Img,
	interpolate,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';

// Define the expected props interface
interface RemotionCompositionProps {
	title?: string;
	images?: string[];
	audioUrl?: string;
	captionJson?: Array<{
		word: string;
		start: number;
		end: number;
		confidence?: number;
	}>;
}

export const RemotionComposition: React.FC<RemotionCompositionProps> = ({
	title,
	images = [],
	audioUrl,
	captionJson = [],
}) => {
	const frame = useCurrentFrame();
	const { fps, durationInFrames } = useVideoConfig();
	const currentTimeInSeconds = frame / fps;

	// Find current caption
	const currentCaption = captionJson.find(
		(caption) =>
			currentTimeInSeconds >= caption.start &&
			currentTimeInSeconds <= caption.end
	);

	// Calculate the scale animation for images
	const scale = interpolate(
		frame,
		[0, durationInFrames / 2, durationInFrames],
		[1, 1.2, 1],
		{
			extrapolateLeft: 'clamp',
			extrapolateRight: 'clamp',
		}
	);

	return (
		<AbsoluteFill style={{ backgroundColor: 'black' }}>
			{/* Display image with animation if available */}
			{images.length > 0 && (
				<AbsoluteFill>
					<Img
						src={images[0]}
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							transform: `scale(${scale})`,
						}}
					/>
				</AbsoluteFill>
			)}

			{/* Display title if provided */}
			{title && (
				<div
					style={{
						position: 'absolute',
						top: '5%',
						width: '100%',
						textAlign: 'center',
						color: 'white',
						fontSize: '48px',
						fontWeight: 'bold',
						textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)',
					}}
				>
					{title}
				</div>
			)}

			{/* Display caption if available */}
			{currentCaption && (
				<div
					style={{
						position: 'absolute',
						bottom: '20%',
						width: '100%',
						textAlign: 'center',
						color: 'white',
						fontSize: '32px',
						fontWeight: 'bold',
						textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
						padding: '20px',
						background: 'rgba(0, 0, 0, 0.5)',
					}}
				>
					{currentCaption.word}
				</div>
			)}

			{/* Play audio if available */}
			{audioUrl && <Audio src={audioUrl} />}

			{/* Debug info */}
			{/* <div
				style={{
					position: 'absolute',
					top: '22px',
					right: '22px',
					color: 'white',
					fontSize: '22px',
					background: 'rgba(0, 0, 0, 0.5)',
					padding: '15px',
					borderRadius: '6px',
				}}
			>
				Frame: {frame} | Time: {currentTimeInSeconds.toFixed(1)}s
			</div> */}
		</AbsoluteFill>
	);
};

export default RemotionComposition;
